import {
  Business, LensSDKException, BusinessHierarchyInvitation, NotFoundException
} from "lens-platform-sdk";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "src/App";
import { useCallback, useState } from "react";
import { useLensPlatformClient } from "./useLensPlatformClient";
import { useAnalytics } from "./useAnalytics";

export const businessChildrenInvitationQueryKey = "businessChildrenInvitation";

/**
 * Hooks for create, cancel, read business (Lens Business IDs) child invitations.
 */
export const useBusinessChildInvitation = (businessId?: Business["id"]) => {
  const lensPlatformClient = useLensPlatformClient();
  const { track } = useAnalytics();

  // See https://tkdodo.eu/blog/react-query-and-type-script#type-safety-with-the-enabled-option
  const fetchChildrenInvitation = useCallback((id?: Business["id"]) => (typeof id === "undefined"
    ? Promise.reject(new Error("Invalid businessId"))
    : lensPlatformClient.business.getManyChildrenInvitation(id)), [lensPlatformClient]);

  const {
    isLoading: loadingBusinessChildrenInvitations,
    error: errorLoadingBusinessChildrenInvitations,
    data: businessChildrenInvitations,
    refetch: refetchBusinessChildrenInvitation
  } = useQuery<BusinessHierarchyInvitation[], LensSDKException>({
    queryKey: [businessChildrenInvitationQueryKey, businessId, fetchChildrenInvitation],
    queryFn: () => fetchChildrenInvitation(businessId),
    enabled: Boolean(businessId),
    retry: (_, error) => {
      if (error instanceof NotFoundException) {
        return false;
      }

      return true;
    }
  });

  const [newlyCreatedChildInvitation, setNewlyCreatedChildInvitation] = useState<BusinessHierarchyInvitation>();
  const {
    isLoading: creatingChildInvitation,
    isSuccess: creatingChildInvitationSuccess,
    error: errorCreatingChildInvitation,
    mutate: createChildInvitation
  } = useMutation<BusinessHierarchyInvitation, LensSDKException, Business["id"]>(
    async (parentBusinessId: Business["id"]) => lensPlatformClient.business.createChildInvitation(parentBusinessId),
    {
      onSuccess: async (invitation) => {
        await queryClient.invalidateQueries({ queryKey: [businessChildrenInvitationQueryKey, businessId] });
        track("BusinessID child invitation created", { businessId });
        setNewlyCreatedChildInvitation(invitation);
      }
    }
  );

  const {
    isLoading: cancelingChildInvitation,
    isSuccess: cancelingChildInvitationSuccess,
    error: errorCancelingChildInvitation,
    mutate: cancelChildInvitation
  } = useMutation<BusinessHierarchyInvitation, LensSDKException, { parentBusinessId: Business["id"], invitationId: BusinessHierarchyInvitation["id"] }>(
    async ({ parentBusinessId, invitationId }) => lensPlatformClient.business.updateOneChildInvitation(
      parentBusinessId, invitationId, "canceled"
    ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [businessChildrenInvitationQueryKey, businessId] });
        track("BusinessID child removed", { businessId });
      }
    }
  );

  const {
    isLoading: acceptingChildInvitation,
    isSuccess: acceptingChildInvitationSuccess,
    error: errorAcceptingChildInvitation,
    mutate: acceptChildInvitation
  } = useMutation<BusinessHierarchyInvitation, LensSDKException, {
    childBusinessId: Business["id"]
    token: BusinessHierarchyInvitation["token"]
  }>(
    async ({ childBusinessId, token }) => lensPlatformClient.business.acceptChildInvitation(
      childBusinessId, "accepted", token
    ),
    {
      onSuccess: async (_, { childBusinessId }) => {
        await queryClient.invalidateQueries({ queryKey: [businessChildrenInvitationQueryKey, businessId] });
        track("BusinessID child invitation accepted", { businessId: childBusinessId });
      }
    }
  );

  const {
    isLoading: rejectingChildInvitation,
    isSuccess: rejectingChildInvitationSuccess,
    error: errorRejectingChildInvitation,
    mutate: rejectChildInvitation
  } = useMutation<BusinessHierarchyInvitation, LensSDKException, {
    childBusinessId: Business["id"]
    token: BusinessHierarchyInvitation["token"]
  }>(
    async ({ childBusinessId, token }) => lensPlatformClient.business.acceptChildInvitation(
      childBusinessId, "rejected", token
    ),
    {
      onSuccess: async (_, { childBusinessId }) => {
        await queryClient.invalidateQueries({ queryKey: [businessChildrenInvitationQueryKey, businessId] });
        track("BusinessID child invitation rejected", { businessId: childBusinessId });
      }
    }
  );

  return {
    loadingBusinessChildrenInvitations,
    errorLoadingBusinessChildrenInvitations,
    businessChildrenInvitations,
    refetchBusinessChildrenInvitation,
    creatingChildInvitation,
    creatingChildInvitationSuccess,
    errorCreatingChildInvitation,
    createChildInvitation,
    newlyCreatedChildInvitation,
    cancelingChildInvitation,
    cancelingChildInvitationSuccess,
    errorCancelingChildInvitation,
    cancelChildInvitation,
    acceptingChildInvitation,
    acceptingChildInvitationSuccess,
    errorAcceptingChildInvitation,
    acceptChildInvitation,
    rejectingChildInvitation,
    rejectingChildInvitationSuccess,
    errorRejectingChildInvitation,
    rejectChildInvitation
  };
};
